/**
 * Remembers the user's light/dark choice in this browser. With no saved choice the operating
 * system's setting decides, as in the reference design.
 * @since NEXT_VERSION
 */
export const THEME_MODE_STORAGE_KEY = "hrms.ui.themeMode";
export const BASE_COLOR_STORAGE_KEY = "hrms.ui.baseColor";

const DARK_QUERY = "(prefers-color-scheme: dark)";

// Storage can be unavailable (private windows, blocked site data), so every access is guarded.
export function readSavedThemeMode() {
  try {
    const value = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch (e) {
    return null;
  }
}

export function saveThemeMode(mode) {
  try {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  } catch (e) {
    // Not saved; the choice still applies for this visit.
  }
}

// "System" is the absence of a saved choice, so the operating system decides again.
export function clearSavedThemeMode() {
  try {
    window.localStorage.removeItem(THEME_MODE_STORAGE_KEY);
  } catch (e) {
    // Nothing saved to clear.
  }
}

export function readSavedBaseColor() {
  try {
    return window.localStorage.getItem(BASE_COLOR_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function saveBaseColor(color) {
  try {
    window.localStorage.setItem(BASE_COLOR_STORAGE_KEY, color);
  } catch (e) {
    // Not saved; the colour still applies for this visit.
  }
}

// matchMedia can be missing or return nothing (old browsers, test environments).
function darkSchemeQuery() {
  return typeof window !== "undefined" && window.matchMedia ? window.matchMedia(DARK_QUERY) : undefined;
}

export function systemPrefersDark() {
  return Boolean(darkSchemeQuery()?.matches);
}

export function initialDarkMode() {
  const saved = readSavedThemeMode();
  return saved ? saved === "dark" : systemPrefersDark();
}

/**
 * Calls back with true/false when the OS theme changes, only while the user has saved no choice.
 * Returns an unsubscribe function.
 */
export function followSystemThemeChanges(onChange) {
  const query = darkSchemeQuery();
  if (!query?.addEventListener) return () => {};
  const listener = (event) => {
    if (!readSavedThemeMode()) onChange(event.matches);
  };
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}
